import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect, useState } from 'react';
import { $getRoot, LexicalNode, $isTextNode } from 'lexical';
import { $isHeadingNode } from '@lexical/rich-text';
import { $isListItemNode } from '@lexical/list';
import EditorCopyButton from '@/components/Editor/EditorCopyButton';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import style from './EditorCopyButtonsPlugin.module.scss';
import { NOTE_SECTION } from '@/constants/note-section.ts';

interface HeadingContent {
  heading: string;
  content: string;
}

export const EditorCopyButtonsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [headings, setHeadings] = useState<HeadingContent[]>([]);
  const [noteContent, setNoteContent] = useState<string>('');

  useEffect(() => {
    handleEditorChange();
  }, [editor]);

  const handleEditorChange = () => {
    editor.getEditorState().read(() => {
      const root = $getRoot();
      const children = root.getChildren();
      const headingContents: HeadingContent[] = [];
      let currentHeading: string | null = null;
      let currentContent: string[] = [];
      const fullContent: string[] = [];

      children.forEach((node: LexicalNode) => {
        const textContent = node.getTextContent();
        fullContent.push(textContent);

        if ($isHeadingNode(node)) {
          if (currentHeading) {
            headingContents.push({ heading: currentHeading, content: currentContent.join('\n') });
          }
          currentHeading = textContent;
          currentContent = [];
        } else if ($isListItemNode(node)) {
          currentContent.push(`- ${textContent}`);
        } else if ($isTextNode(node) && node.hasFormat('bold')) {
          currentContent.push(`**${textContent}**`);
        } else if (currentHeading) {
          currentContent.push(textContent);
        }
      });

      if (currentHeading) {
        headingContents.push({ heading: currentHeading, content: currentContent.join('\n') });
      }

      setHeadings(headingContents);
      setNoteContent(fullContent.join('\n'));
    });
  };

  return (
    <div className={style.lexical__editor__copy_button_list}>
      <OnChangePlugin onChange={handleEditorChange} />
      <EditorCopyButton
        section="Note"
        content={noteContent}
      />
      {headings.map((headingContent, index) => {
        if (!Object.values(NOTE_SECTION).includes(headingContent.heading.trim())) {
          return null;
        }
        return (
          <EditorCopyButton
            key={index}
            section={headingContent.heading}
            content={headingContent.content}
          />
        );
      })}
    </div>
  );
};

export default EditorCopyButtonsPlugin;
